"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useEffect, useState, useSyncExternalStore } from "react";
import type { EvidenceReview } from "./types";
import { EvidenceReviewCard } from "./evidence-review-card";
import { TranslatedContent, useLanguage } from '@/shared/i18n/provider';
import { formatMessage, translate } from '@/shared/i18n/catalog';
interface Props {
  topic: string;
  label: string;
  reviews: EvidenceReview[];
  sectionTitles: string[];
  treatment?: {
    slug: string;
    name: string;
  };
  scopeTitle?: string;
  scopeCountLine?: string;
  scopeNote?: string;
  basisOptions?: string[][];
  searchPlaceholder?: string;
  pageSize?: number;
  stats: {
    corpusUniqueTexts: number;
    reviewed: number;
    published: number;
    excluded: number;
  };
}
const chip = "rounded-pill border px-[12px] py-[9px] text-[12px] leading-[1.45] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-mint-400";
const chipState = (active: boolean) => `${chip} ${active ? "border-accent-mint-400 bg-surface-4 text-accent-mint-300" : "border-line-3 text-ink-3"}`;
const subscribeLocation = (listener: () => void) => {
  window.addEventListener("popstate", listener);
  window.addEventListener("hashchange", listener);
  return () => {
    window.removeEventListener("popstate", listener);
    window.removeEventListener("hashchange", listener);
  };
};
const requestedSection = (sectionCount: number) => {
  const value = Number(new URLSearchParams(window.location.search).get("section"));
  return Number.isInteger(value) && value >= 1 && value <= sectionCount ? value : null;
};

/** All selected masked full texts are present; review status remains explicit. No sample cap. */
export function TreatmentEvidenceExplorer(t0) {
  const $ = _c(95);
  const {
    topic,
    label,
    reviews,
    sectionTitles,
    stats,
    treatment: t1,
    scopeTitle,
    scopeCountLine,
    scopeNote,
    pageSize,
    searchPlaceholder: t2,
    basisOptions: t3
  } = t0;
  const treatment = t1 === undefined ? {
    slug: "thermage",
    name: "\uC368\uB9C8\uC9C0"
  } : t1;
  const searchPlaceholder = t2 === undefined ? "\uC608: \uD131\uC120, \uC218\uBA74, \uB2E4\uC74C \uB0A0" : t2;
  let t4;
  if ($[0] !== t3) {
    t4 = t3 === undefined ? [["all", "\uBAA8\uB4E0 \uADFC\uAC70"], ["experience", "\uC2E4\uC81C \uACBD\uD5D8"], ["context", "\uC870\uAC74\xB7\uC804\uC5B8 \uCC38\uACE0"]] : t3;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const basisOptions = t4;
  const {
    catalog
  } = useLanguage();
  let t5;
  if ($[2] !== sectionTitles.length) {
    t5 = () => requestedSection(sectionTitles.length);
    $[2] = sectionTitles.length;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const fromUrl = useSyncExternalStore(subscribeLocation, t5, _temp);
  const anchor = useSyncExternalStore(subscribeLocation, _temp2, _temp3);
  const [override, setSection] = useState(undefined);
  const section = override === undefined ? fromUrl : override;
  const [source, setSource] = useState("\uC804\uCCB4");
  const [search, setSearch] = useState("");
  const [basis, setBasis] = useState("all");
  const t6 = pageSize ?? Infinity;
  let t7;
  if ($[4] !== t6) {
    t7 = {
      key: "",
      limit: t6
    };
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const [pagination, setPagination] = useState(t7);
  let t8;
  if ($[6] !== anchor || $[7] !== fromUrl || $[8] !== topic) {
    t8 = [fromUrl, topic, anchor];
    $[6] = anchor;
    $[7] = fromUrl;
    $[8] = topic;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  useEffect(_temp4, t8);
  let t9;
  if ($[10] !== reviews) {
    t9 = new Set(reviews.map(_temp5));
    $[10] = reviews;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t9) {
    t10 = ["\uC804\uCCB4", ...t9];
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const sources = t10;
  let t11;
  if ($[14] !== basis || $[15] !== search || $[16] !== section || $[17] !== source || $[18] !== topic) {
    t11 = review_0 => {
      const memberships = review_0.memberships.filter(member => section === null || member.slot === `${topic}-0${section}`);
      return memberships.some(member_0 => basis === "all" || member_0.basis === basis) && (source === "\uC804\uCCB4" || review_0.source === source) && `${review_0.text} ${review_0.originalText ?? ""}`.replace(/\s+/g, " ").toLocaleLowerCase().includes(search.trim().replace(/\s+/g, " ").toLocaleLowerCase());
    };
    $[14] = basis;
    $[15] = search;
    $[16] = section;
    $[17] = source;
    $[18] = topic;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const filtered = reviews.filter(t11);
  let t12;
  if ($[20] !== pageSize || $[21] !== topic) {
    t12 = () => {
      setSection(null);
      setSource("\uC804\uCCB4");
      setSearch("");
      setBasis("all");
      setPagination({
        key: JSON.stringify([topic, null, "\uC804\uCCB4", "", "all"]),
        limit: pageSize ?? Infinity
      });
    };
    $[20] = pageSize;
    $[21] = topic;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const reset = t12;
  let t13;
  if ($[23] !== basis || $[24] !== search || $[25] !== section || $[26] !== source || $[27] !== topic) {
    t13 = JSON.stringify([topic, section, source, search, basis]);
    $[23] = basis;
    $[24] = search;
    $[25] = section;
    $[26] = source;
    $[27] = topic;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const filterKey = t13;
  let t14;
  if ($[29] !== anchor) {
    t14 = review_1 => `evidence-${review_1.key}` === anchor;
    $[29] = anchor;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const anchorIndex = filtered.findIndex(t14);
  const initialLimit = pageSize ? Math.max(pageSize, Math.ceil((anchorIndex + 1) / pageSize) * pageSize) : Infinity;
  const limit = pagination.key === filterKey ? pagination.limit : initialLimit;
  const visible = filtered.slice(0, limit);
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span>›</span>;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = `/${treatment.slug}/v1/${topic}`;
  let t17;
  if ($[32] !== label || $[33] !== t16) {
    t17 = <Link href={t16}>{label}</Link>;
    $[32] = label;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span>›</span>;
    t19 = <span>근거 후기</span>;
    $[35] = t18;
    $[36] = t19;
  } else {
    t18 = $[35];
    t19 = $[36];
  }
  let t20;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <br />;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== catalog || $[39] !== reviews.length) {
    t21 = formatMessage("\uD6C4\uAE30 {count}\uAC1C", {
      count: reviews.length
    }, catalog);
    $[38] = catalog;
    $[39] = reviews.length;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t21) {
    t22 = <h1 className="text-[26px] font-bold leading-[1.4] tracking-[-0.7px] text-ink-1 md:text-[38px]">목차별로 모아 읽는{t20}<span>{t21}</span></h1>;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <p className="max-w-[700px] text-[14px] leading-[1.7] text-ink-3">대표 인용뿐 아니라 같은 목차에 분류한 후기를 모두 열어뒀어요. 문장 앞뒤를 함께 읽을 수 있도록 전문을 싣고, 이름·병원·연락처 등은 마스킹했습니다. 한 후기가 여러 목차에 속해도 전체 건수에서는 한 번만 셉니다. 왼쪽에서 목차를 고르면 그 목차의 근거가 된 원문 구간이 후기마다 강조돼요.</p>;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  const t24 = scopeTitle ?? `${treatment.name} 4편 전체의 확인 범위`;
  let t25;
  if ($[44] !== t24) {
    t25 = <p className="font-bold text-ink-2">{t24}</p>;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== catalog || $[47] !== scopeCountLine || $[48] !== stats) {
    t26 = scopeCountLine ?? formatMessage("\uC5B8\uAE09 \uD6C4\uAE30 {total}\uAC1C\uC5D0\uC11C \uD6C4\uBCF4 \uD0D0\uC0C9 \u2192 \uC6D0\uBB38 \uAC80\uD1A0 {reviewed}\uAC1C \u2192 \uACF5\uAC1C {published}\uAC1C \xB7 \uC81C\uC678/\uBCF4\uB958 {excluded}\uAC1C", {
      total: stats.corpusUniqueTexts.toLocaleString("ko-KR"),
      reviewed: stats.reviewed,
      published: stats.published,
      excluded: stats.excluded
    }, catalog);
    $[46] = catalog;
    $[47] = scopeCountLine;
    $[48] = stats;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t26) {
    t27 = <p>{t26}</p>;
    $[50] = t26;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== catalog || $[53] !== label || $[54] !== reviews.length || $[55] !== scopeNote) {
    t28 = scopeNote ?? formatMessage("\uC9C0\uAE08 \uBCF4\uB294 {topic} \uD3B8\uC5D0\uB294 {count}\uAC1C\uAC00 \uC5F0\uACB0\uB3FC \uC788\uC5B4\uC694. \uAC80\uC0C9 \uB300\uC0C1 \uC804\uBD80\uB97C \uAC80\uC218\uD588\uB2E4\uB294 \uB73B\uC740 \uC544\uB2D9\uB2C8\uB2E4. \uD1B5\uACC4\uC801 \uB300\uD45C \uD45C\uBCF8\uC774\uB098 \uC2DC\uC220\uC790 \uC218\uAC00 \uC544\uB2CC, \uBAA9\uCC28\uBCC4\uB85C \uC120\uBCC4\uD55C \uAC1C\uC778 \uACBD\uD5D8\uC785\uB2C8\uB2E4.", {
      topic: translate(label, catalog),
      count: reviews.length
    }, catalog);
    $[52] = catalog;
    $[53] = label;
    $[54] = reviews.length;
    $[55] = scopeNote;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== t28) {
    t29 = <p>{t28}</p>;
    $[57] = t28;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== t25 || $[60] !== t27 || $[61] !== t29) {
    t30 = <div className="rounded-[14px] border border-line-2 bg-surface-2 p-[16px] text-12 leading-[1.8] text-ink-3">{t25}{t27}{t29}</div>;
    $[59] = t25;
    $[60] = t27;
    $[61] = t29;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <p className="mb-[10px] text-12 font-bold text-ink-3">목차별 후기 묶음</p>;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== catalog || $[65] !== reviews || $[66] !== section || $[67] !== sectionTitles) {
    t32 = <div>{t31}<div className="flex flex-wrap gap-[6px]">{["\uC804\uCCB4", ...sectionTitles].map((title, i) => {
          const count = i === 0 ? reviews.length : reviews.filter(review_2 => review_2.sections.includes(i)).length;
          return <button type="button" key={title} aria-pressed={section === (i === 0 ? null : i)} onClick={() => setSection(i === 0 ? null : i)} className={chipState(section === (i === 0 ? null : i))}>{formatMessage("{title} \xB7 {count}", {
              title: i === 0 ? translate(title, catalog) : `${String(i).padStart(2, "0")} ${translate(title, catalog)}`,
              count
            }, catalog)}</button>;
        })}</div></div>;
    $[64] = catalog;
    $[65] = reviews;
    $[66] = section;
    $[67] = sectionTitles;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = event => setSearch(event.target.value);
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== search || $[71] !== searchPlaceholder) {
    t34 = <label className="flex flex-col gap-[8px] text-12 text-ink-3">후기 전문에서 검색<input type="search" value={search} onChange={t33} placeholder={searchPlaceholder} className="min-h-[44px] w-full rounded-[10px] border border-line-3 bg-surface-base px-[12px] text-14 text-ink-1" /></label>;
    $[70] = search;
    $[71] = searchPlaceholder;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== basis || $[74] !== basisOptions) {
    let t36;
    if ($[76] !== basis) {
      t36 = t37 => {
        const [value, name] = t37;
        return <button type="button" key={value} aria-pressed={basis === value} onClick={() => setBasis(value)} className={chipState(basis === value)}>{name}</button>;
      };
      $[76] = basis;
      $[77] = t36;
    } else {
      t36 = $[77];
    }
    t35 = basisOptions.map(t36);
    $[73] = basis;
    $[74] = basisOptions;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[78] !== t35) {
    t36 = <div className="flex flex-wrap gap-[6px]" aria-label={"\uADFC\uAC70 \uC131\uACA9"}>{t35}</div>;
    $[78] = t35;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <p className="mb-[10px] text-12 font-bold text-ink-3">출처</p>;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== source || $[82] !== sources) {
    t38 = <div>{t37}<div className="flex flex-wrap gap-[6px]">{sources.map(name_0 => <button type="button" key={name_0} aria-pressed={source === name_0} onClick={() => setSource(name_0)} className={chipState(source === name_0)}>{name_0}</button>)}</div></div>;
    $[81] = source;
    $[82] = sources;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== reset) {
    t39 = <button type="button" onClick={reset} className="min-h-[40px] text-left text-12 text-accent-mint-400 underline underline-offset-4">필터 초기화</button>;
    $[84] = reset;
    $[85] = t39;
  } else {
    t39 = $[85];
  }
  const t40 = "min-w-0 px-[16px] md:px-0";
  const t41 = filtered.length === 0 ? <p className="py-[40px] text-ink-3">이 조건의 근거는 없어요. 다른 목차나 출처를 골라주세요.</p> : null;
  const t42 = visible.map(review_3 => <EvidenceReviewCard key={`${review_3.key}:${section}:${basis}`} review={review_3} topic={topic} treatmentSlug={treatment.slug} sectionTitles={sectionTitles} slot={section === null ? review_3.memberships.find(member_1 => basis === "all" || member_1.basis === basis).slot : `${topic}-0${section}`} />);
  const t43 = visible.length < filtered.length && <button type="button" onClick={() => setPagination({
    key: filterKey,
    limit: limit + (pageSize ?? filtered.length)
  })} className="my-[16px] min-h-[48px] w-full rounded-[14px] border border-line-3 bg-surface-2 px-[16px] py-[15px] text-14 text-accent-mint-400">{formatMessage("\uADFC\uAC70 \uD6C4\uAE30 \uB354 \uBCF4\uAE30 ({visible}/{total})", {
      visible: visible.length,
      total: filtered.length
    }, catalog)}</button>;
  const T0 = Link;
  const t44 = `/${treatment.slug}/v1/${topic}`;
  const t45 = "my-[24px] flex min-h-[48px] items-center justify-center rounded-[14px] bg-surface-paper-2 px-[16px] py-[15px] text-14 font-bold text-ink-on-accent";
  const t46 = formatMessage("{treatment} {topic} \uAE00\uB85C \uB3CC\uC544\uAC00\uAE30 \u2192", {
    treatment: translate(treatment.name, catalog),
    topic: translate(label, catalog)
  }, catalog);
  let t47;
  if ($[86] !== T0 || $[87] !== t44 || $[88] !== t46) {
    t47 = <T0 href={t44} className={t45}>{t46}</T0>;
    $[86] = T0;
    $[87] = t44;
    $[88] = t46;
    $[89] = t47;
  } else {
    t47 = $[89];
  }
  let t48;
  if ($[90] !== t41 || $[91] !== t42 || $[92] !== t43 || $[93] !== t47) {
    t48 = <div className={t40}>{t41}{t42}{t43}{t47}</div>;
    $[90] = t41;
    $[91] = t42;
    $[92] = t43;
    $[93] = t47;
    $[94] = t48;
  } else {
    t48 = $[94];
  }
  return <TranslatedContent><section className="flex flex-col gap-[16px] px-[16px] pb-[28px] pt-[26px] md:px-[32px] md:pb-[42px] md:pt-[38px]"><nav className="flex items-center gap-[7px] text-[12px] font-medium text-ink-4" aria-label={"\uD604\uC7AC \uC704\uCE58"}><Link href={`/${treatment.slug}/v1/effect`}>{treatment.name}</Link>{t15}{t17}{t18}{t19}</nav>{t22}{t23}{t30}</section><div className="md:grid md:grid-cols-[320px_minmax(0,1fr)] md:items-start md:gap-[40px] md:px-[32px] md:pb-[48px] md:pt-[32px]"><aside className="flex flex-col gap-[20px] px-[16px] pb-[20px] pt-[22px] md:sticky md:top-[72px] md:max-h-[calc(100dvh-90px)] md:overflow-y-auto md:rounded-[18px] md:border md:border-line-2 md:px-[20px]" aria-label={"\uADFC\uAC70 \uD6C4\uAE30 \uD544\uD130"}>{t32}{t34}{t36}{t38}<p className="text-12 text-ink-3" role="status">{pageSize ? formatMessage("\uC870\uAC74\uC5D0 \uB9DE\uB294 \uD6C4\uAE30 {count}\uAC1C \xB7 {visible}\uAC1C \uD45C\uC2DC \xB7 \uC804\uCCB4 \uBAA9\uB85D\uC5D0\uC11C \uAC80\uC0C9", {
            count: filtered.length,
            visible: visible.length
          }, catalog) : `조건에 맞는 실제 후기 ${filtered.length}개 · 전문 전체 표시`}</p>{t39}</aside>{t48}</div></TranslatedContent>;
}
function _temp5(review) {
  return review.source;
}
function _temp4() {
  const id = window.location.hash.slice(1);
  if (!id.startsWith("evidence-")) {
    return;
  }
  const frame = requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView());
  return () => cancelAnimationFrame(frame);
}
function _temp3() {
  return "";
}
function _temp2() {
  return window.location.hash.slice(1);
}
function _temp() {
  return null;
}